import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { ConsecutiveMasterSchedule } from "./consecutive/ConsecutiveMasterSchedule";
import { LONG_PTO_BUTTON_INSET_PX } from "./LongPTOButton/consts";
import { LongPTOButton } from "./LongPTOButton/LongPTOButton";
import { ScheduleTemplatePageTab, ScheduleTemplateTabs, useScheduleTemplateTab } from "./ScheduleTemplateTabs";
import { MasterScheduleV2 } from "./v2/MasterScheduleV2";
import { MasterScheduleCoverage } from "./v3/MasterScheduleCoverage";

/**
 * Entry point for the Schedule Template route. Renders the weekly (MasterScheduleV2), consecutive
 * (4/2), or coverage layout based on the persisted, flag-gated tab selection, and injects the shared
 * Weekly/Consecutive/Coverage/Locations tab strip into the active view's header via its `trailing` slot. The
 * People axis has moved to the Staffing area (see `StaffingPeopleView`), so this wrapper no longer
 * mounts it.
 */
export const ScheduleTemplatePageWrapper = () => {
	const navigate = useNavigate();
	const { chosenDepartment } = useChosenDepartment();
	const { selectedTab, setSelectedTab, isConsecutiveEnabled, isCoverageEnabled } = useScheduleTemplateTab();

	// Locations is the strip's last tab: the layout tabs switch the view you are already in, while Locations
	// leaves for the page of the same name (which shows the same strip with Locations selected).
	const tabs = (
		<ScheduleTemplateTabs
			selectedTab={selectedTab}
			onChange={setSelectedTab}
			onOpenLocations={() => navigate(`/${chosenDepartment.department_id}/locations`)}
			isConsecutiveEnabled={isConsecutiveEnabled}
			isCoverageEnabled={isCoverageEnabled}
		/>
	);

	const view = ((): ReactNode => {
		if (selectedTab === ScheduleTemplatePageTab.Coverage) return <MasterScheduleCoverage trailing={tabs} />;
		if (selectedTab === ScheduleTemplatePageTab.Consecutive) return <ConsecutiveMasterSchedule trailing={tabs} />;
		return <MasterScheduleV2 trailing={tabs} />;
	})();

	return (
		<>
			{view}
			<Box sx={{ position: "fixed", bottom: LONG_PTO_BUTTON_INSET_PX, left: LONG_PTO_BUTTON_INSET_PX, zIndex: 4 }}>
				<LongPTOButton />
			</Box>
		</>
	);
};
